import {useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';
import { useToast } from 'hooks/use-toast';
import { useGlobalMutation, useGlobalQuery } from 'state/query-client/hooks';
import {
  changePassword,
  createAccount,
  getAccount,
  updateAccount,
} from '../services/accounts-service';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'hooks/use-config';
import {
  CreateUserFormType,
  ProfileFormType,
} from 'features/profile/utils/utils';
import {User} from "/types/user-type";

/**
 * Custom hook to fetch account details.
 * It uses a global query to fetch data related to the account.
 *
 * @returns {Object} The query result for getting the account.
 *
 * @example
 * const { data, error, isLoading } = useGetAccount();
 */
export const useGetAccount = (option: any): UseQueryResult<User> => {
  const { configurationSettings } = useConfig();
  return useGlobalQuery({
    queryKey: ['getAccount'],
    queryFn: () => getAccount(configurationSettings),
    ...option
  });
};

/**
 * Custom hook to create a new account.
 * It uses a global mutation to create a new account and handle success or error states.
 *
 * @param {Object} [options] - Optional configuration for mutation.
 * @param {Function} [options.onSuccess] - Callback function to be called when the account creation is successful.
 *
 * @returns {Object} The mutation result for creating the account.
 *
 * @example
 * const { mutate } = useCreateAccount({
 *   onSuccess: () => {
 *     // handle success logic
 *   }
 * });
 */

export const useCreateAccount = (options?: { onSuccess?: () => void }) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();

  return useGlobalMutation({
    mutationKey: ['createAccount'],
    mutationFn: (data: CreateUserFormType) => createAccount(data, configurationSettings),
    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('USER_ADD_SUCCESS_DESCRIPTION'),
      });

      options?.onSuccess?.();
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: t('SOMETHING_WENT_WRONG'),
        description: error?.error?.message ?? t('USER_ADD_FAILED_DESCRIPTION'),
      });
    },
  });
};

/**
 * Custom hook to update account details.
 * It uses a global mutation to update the account information and handle success or error states.
 * It also invalidates the account query cache upon success.
 *
 * @param {Object} [options] - Optional configuration for mutation.
 * @param {Function} [options.onSuccess] - Callback function to be called when the account update is successful.
 *
 * @returns {Object} The mutation result for updating the account.
 *
 * @example
 * const { mutate } = useUpdateAccount({
 *   onSuccess: () => {
 *     // handle success logic
 *   }
 * });
 */
export const useUpdateAccount = (options?: { onSuccess?: () => void }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();

  return useGlobalMutation({
    mutationKey: ['updateAccount'],
    mutationFn: (data: ProfileFormType) => updateAccount(data, configurationSettings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getAccount'] });
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('PROFILE_UPDATE_SUCCESS'),
      });

      options?.onSuccess?.();
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: t('SOMETHING_WENT_WRONG'),
        description:
          error?.error?.message ?? `${t('PROFILE_UPDATE_FAILED')} ${t('PLEASE_CHECK_INPUT')}`,
      });
    },
  });
};

/**
 * Custom hook to change the user's password.
 * It uses a global mutation to update the password and display appropriate success or error messages.
 *
 * @returns {Object} The mutation result for changing the password.
 *
 * @example
 * const { mutate } = useChangePassword();
 */
export const useChangePassword = () => {
  const { toast } = useToast();
  const { configurationSettings } = useConfig();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationKey: ['changePassword'],
    mutationFn: (data: { oldPassword: string; newPassword: string }) =>
      changePassword({
        ...data,
        config: configurationSettings,
      }),
    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('PASSWORD_UPDATE_SUCCESS'),
      });
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: t('SOMETHING_WENT_WRONG'),
        description: t('CHECK_PASSWORD'),
      });
    },
  });
};

export const ACCOUNT_QUERY_KEY = ['account'];

/**
 * Custom hook to fetch the account details using a query.
 *
 * @returns {Object} The query result for fetching the account.
 *
 * @example
 * const { data, error, isLoading } = useAccountQuery();
 */
export const useAccountQuery = () => {
  const { configurationSettings } = useConfig();

  return useQuery({
    queryKey: ACCOUNT_QUERY_KEY,
    queryFn: () => getAccount(configurationSettings),
  });
};
